import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, ChevronDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import LockerFloorFormSlider from '../components/LockerFloorFormSlider';
import LockerAddonFormSlider from '../components/LockerAddonFormSlider';
import LockerTypeFormSlider from '../components/LockerTypeFormSlider';
import LockerFloorDetailsSlider from '../components/LockerFloorDetailsSlider';
import { lockerFloorService, lockerAddonService, lockerTypeService } from '../services/lockerService';
import { LockerFloor, LockerAddon, LockerType } from '../types/locker';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Lockers: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lockerFloors, setLockerFloors] = useState<LockerFloor[]>([]);
  const [lockerAddons, setLockerAddons] = useState<LockerAddon[]>([]);
  const [lockerTypes, setLockerTypes] = useState<LockerType[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedLockerFloorId, setSelectedLockerFloorId] = useState<string | null>(null);

  // Delete modals state
  const [showDeleteFloorModal, setShowDeleteFloorModal] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<LockerFloor | null>(null);
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState<LockerAddon | null>(null);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<LockerType | null>(null);

  // Get drawer state from URL params
  const isFloorDrawerOpen = searchParams.get('action') === 'new-floor' || (!!searchParams.get('edit') && searchParams.get('type') === 'floor');
  const editFloorId = searchParams.get('type') === 'floor' ? searchParams.get('edit') : null;

  const isAddonDrawerOpen = searchParams.get('action') === 'new-addon' || (!!searchParams.get('edit') && searchParams.get('type') === 'addon');
  const editAddonId = searchParams.get('type') === 'addon' ? searchParams.get('edit') : null;

  const isTypeDrawerOpen = searchParams.get('action') === 'new-type' || (!!searchParams.get('edit') && searchParams.get('type') === 'type');
  const editTypeId = searchParams.get('type') === 'type' ? searchParams.get('edit') : null;

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProject]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);

      // Automatically select the first project if no project is selected
      if (response.data.projects.length > 0 && !selectedProject) {
        setSelectedProject(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchAllData = async () => {
    if (!workspaceId || !selectedProject) return;

    try {
      setLoading(true);
      setError('');

      const params: any = {
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
        projectId: selectedProject
      };

      const [floorsResponse, addonsResponse, typesResponse] = await Promise.all([
        lockerFloorService.getAll(workspaceId, params),
        lockerAddonService.getAll(workspaceId, params),
        lockerTypeService.getAll(workspaceId, params)
      ]);

      setLockerFloors(floorsResponse.lockerFloors);
      setLockerAddons(addonsResponse.lockerAddons);
      setLockerTypes(typesResponse.lockerTypes);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch locker data');
      console.error('Error fetching locker data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAddFloor = () => {
    setSearchParams({ action: 'new-floor' });
  };

  const handleAddAddon = () => {
    setSearchParams({ action: 'new-addon' });
  };

  const handleAddType = () => {
    setSearchParams({ action: 'new-type' });
  };

  const handleEditFloor = (id: string) => {
    setSearchParams({ edit: id, type: 'floor' });
  };

  const handleEditAddon = (id: string) => {
    setSearchParams({ edit: id, type: 'addon' });
  };

  const handleEditType = (id: string) => {
    setSearchParams({ edit: id, type: 'type' });
  };

  const handleCloseFloorDrawer = () => {
    setSearchParams({});
  };

  const handleFloorSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleCloseAddonDrawer = () => {
    setSearchParams({});
  };

  const handleAddonSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleCloseTypeDrawer = () => {
    setSearchParams({});
  };

  const handleTypeSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleDeleteFloorClick = (floor: LockerFloor) => {
    setFloorToDelete(floor);
    setShowDeleteFloorModal(true);
  };

  const handleDeleteFloorConfirm = async () => {
    if (!floorToDelete || !workspaceId) return;

    try {
      await lockerFloorService.delete(workspaceId, floorToDelete._id);
      setShowDeleteFloorModal(false);
      setFloorToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete locker floor');
      console.error('Error deleting locker floor:', err);
    }
  };

  const handleDeleteAddonClick = (addon: LockerAddon) => {
    setAddonToDelete(addon);
    setShowDeleteAddonModal(true);
  };

  const handleDeleteAddonConfirm = async () => {
    if (!addonToDelete || !workspaceId) return;

    try {
      await lockerAddonService.delete(workspaceId, addonToDelete._id);
      setShowDeleteAddonModal(false);
      setAddonToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete locker add-on');
      console.error('Error deleting locker add-on:', err);
    }
  };

  const handleDeleteTypeClick = (type: LockerType) => {
    setTypeToDelete(type);
    setShowDeleteTypeModal(true);
  };

  const handleDeleteTypeConfirm = async () => {
    if (!typeToDelete || !workspaceId) return;

    try {
      await lockerTypeService.delete(workspaceId, typeToDelete._id);
      setShowDeleteTypeModal(false);
      setTypeToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete locker type');
      console.error('Error deleting locker type:', err);
    }
  };

  const handleFloorRowClick = (floorId: string) => {
    setSelectedLockerFloorId(floorId);
  };

  const handleCloseLockerFloorDetails = () => {
    setSelectedLockerFloorId(null);
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Locker</h2>
                <p className="builders-section-subtitle">
                  Manage locker floors, add-ons, and types
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  className="form-input"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ width: '250px' }}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Locker Floors Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Locker Floors</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddFloor}>
                  Add Floor
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockerFloors.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No locker floors found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      lockerFloors.map((floor) => (
                        <tr
                          key={floor._id}
                          onClick={() => handleFloorRowClick(floor._id)}
                        >
                          <td><strong>{floor.projectName || '-'}</strong></td>
                          <td><strong>{floor.name}</strong></td>
                          <td>{floor.total}</td>
                          <td>{floor.available}</td>
                          <td>{floor.marketingHold}</td>
                          <td>{floor.salesHold}</td>
                          <td>{floor.sold}</td>
                          <td>{floor.customerSelection}</td>
                          <td>{floor.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFloor(floor._id);
                                }}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFloorClick(floor);
                                }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Locker Add-ons Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Locker Add-ons</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddAddon}>
                  Add Add-on
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Capable</th>
                      <th>Sold</th>
                      <th>Cost</th>
                      <th>Price</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockerAddons.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No locker add-ons found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      lockerAddons.map((addon) => (
                        <tr key={addon._id}>
                          <td><strong>{addon.projectName || '-'}</strong></td>
                          <td><strong>{addon.name}</strong></td>
                          <td>{addon.total}</td>
                          <td>{addon.available}</td>
                          <td>{addon.capable}</td>
                          <td>{addon.sold}</td>
                          <td>${addon.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>${addon.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditAddon(addon._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteAddonClick(addon)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Locker Types Table */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Locker Types</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddType}>
                  Add Type
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockerTypes.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No locker types found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      lockerTypes.map((type) => (
                        <tr key={type._id}>
                          <td><strong>{type.projectName || '-'}</strong></td>
                          <td><strong>{type.name}</strong></td>
                          <td>{type.total}</td>
                          <td>{type.available}</td>
                          <td>{type.marketingHold}</td>
                          <td>{type.salesHold}</td>
                          <td>{type.sold}</td>
                          <td>{type.customerSelection}</td>
                          <td>{type.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditType(type._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteTypeClick(type)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Locker Floor Create/Edit */}
        <Drawer
          isOpen={isFloorDrawerOpen}
          onClose={handleCloseFloorDrawer}
          title={editFloorId ? 'Edit Locker Floor' : 'Add Locker Floor'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseFloorDrawer} form="locker-floor-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="locker-floor-form">
                {editFloorId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isFloorDrawerOpen && (
            <LockerFloorFormSlider
              workspaceId={workspaceId!}
              floorId={editFloorId || undefined}
              onSuccess={handleFloorSuccess}
              onCancel={handleCloseFloorDrawer}
            />
          )}
        </Drawer>

        {/* Drawer for Locker Add-on Create/Edit */}
        <Drawer
          isOpen={isAddonDrawerOpen}
          onClose={handleCloseAddonDrawer}
          title={editAddonId ? 'Edit Locker Add-on' : 'Add Locker Add-on'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseAddonDrawer} form="locker-addon-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="locker-addon-form">
                {editAddonId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isAddonDrawerOpen && (
            <LockerAddonFormSlider
              workspaceId={workspaceId!}
              addonId={editAddonId || undefined}
              onSuccess={handleAddonSuccess}
              onCancel={handleCloseAddonDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal - Locker Floor */}
        {showDeleteFloorModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteFloorModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Locker Floor</h3>
              <p>Are you sure you want to delete "{floorToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteFloorModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteFloorConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Locker Add-on */}
        {showDeleteAddonModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteAddonModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Locker Add-on</h3>
              <p>Are you sure you want to delete "{addonToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteAddonModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteAddonConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Locker Type */}
        {showDeleteTypeModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteTypeModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Locker Type</h3>
              <p>Are you sure you want to delete "{typeToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteTypeModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteTypeConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Locker Type Create/Edit */}
        <Drawer
          isOpen={isTypeDrawerOpen}
          onClose={handleCloseTypeDrawer}
          title={editTypeId ? 'Edit Locker Type' : 'Add Locker Type'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseTypeDrawer} form="locker-type-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="locker-type-form">
                {editTypeId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isTypeDrawerOpen && (
            <LockerTypeFormSlider
              workspaceId={workspaceId!}
              typeId={editTypeId || undefined}
              onSuccess={handleTypeSuccess}
              onCancel={handleCloseTypeDrawer}
            />
          )}
        </Drawer>

        {/* Locker Floor Details Slider */}
        {selectedLockerFloorId && workspaceId && (
          <LockerFloorDetailsSlider
            lockerFloorId={selectedLockerFloorId}
            workspaceId={workspaceId}
            onClose={handleCloseLockerFloorDetails}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Lockers;
